import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { componentMapping, settingsConfig } from '../../views/settings/SettingsConfig';
import { useOrderableModuleFeatures } from '../../views/settings/billing/functions/useOrderableModuleFeatures';
import PageHeader from 'components/layout/PageHeader';
import ModuleOrderLink from 'components/billing/ModuleOrderLink';
import ModuleOrderCard from '../../views/settings/billing/ModuleOrderCard';

type SettingsPageConfig = typeof settingsConfig[keyof typeof settingsConfig]['items'][number];

const SettingsPage: React.FC = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const { packageFeatures, activeFeatures, environmentType } = useAuthContext();
    const moduleFeatures = useOrderableModuleFeatures();
    const currentPath = location.pathname;

    // Find the right configuration based on the current route
    // let currentPageConfig;
    // for (const group of Object.values(settingsConfig)) {
    //     const foundItem = group.items.find(item => item.route === currentPath);
    //     if (foundItem) {
    //         currentPageConfig = foundItem;
    //         break;
    //     }
    // }

    let currentPageConfig: SettingsPageConfig | undefined;

    for (const group of Object.values(settingsConfig)) {
        const foundItem = group.items.find(item => {
            return (
                currentPath === item.route ||
                currentPath.startsWith(`${item.route}/`)
            );
        });

        if (foundItem) {
            currentPageConfig = foundItem;
            break;
        }
    }

    if (!currentPageConfig) {
        // No settings page configured for the current route
        return null;
    }

    const currentPageRoute = currentPageConfig.route;

    // Determine the visibility state of every page header on this page
    const headerConfigs = (currentPageConfig.pageHeader ?? [])
        .map((headerConfig, index) => {

            // Check if the feature is included in the environment package
            const inPackage = headerConfig.requiredFeature
                ? packageFeatures.includes(headerConfig.requiredFeature)
                : true;

            // Check if the feature is active within the environment
            const isFeatureActive = headerConfig.requiredFeature
                ? activeFeatures.includes(headerConfig.requiredFeature)
                : false;

            // Check if the feature is available to switch within the environment
            const isAvailable = headerConfig.requiredFeature
                ? !!moduleFeatures?.availableFeatures.includes(headerConfig.requiredFeature)
                : true;

            // Check if the feature is orderable as module within the current package
            const isOrderable = headerConfig.requiredFeature
                ? !!moduleFeatures?.orderableFeatures.includes(headerConfig.requiredFeature)
                : false;

            // Check if the feature is unreleased
            const isUnreleased = headerConfig.requiredFeature
                ? !!moduleFeatures?.unreleasedFeatures.includes(headerConfig.requiredFeature)
                : false;

            return { headerConfig, index, inPackage, isFeatureActive, isAvailable, isOrderable, isUnreleased };
        })
        .filter(({ headerConfig, inPackage, isFeatureActive, isAvailable, isOrderable, isUnreleased }) => {

            // Hide module cards of modules which are already active
            if (headerConfig.moduleCard && isFeatureActive) {
                return false;
            }

            // Hide module cards of unreleased features
            if (headerConfig.moduleCard && isUnreleased) {
                return false;
            }

            // Hide orderable module cards for trials, they order modules within the subscription wizard
            if (headerConfig.moduleCard && isOrderable && !isAvailable && environmentType === 'trial') {
                return false;
            }

            // Hide unavailable features, except module cards which show an order badge
            if (!inPackage && !headerConfig.moduleCard) {
                return false;
            }

            return true;
        })
        // Sort the module cards on available, orderable and upgrade
        .sort((a, b) => {
            const cardOrder = (card: typeof a) => card.isAvailable ? 0 : card.isOrderable ? 1 : 2;
            return cardOrder(a) - cardOrder(b);
        });

    // Determine the section of a module card for the section headers
    const sectionOf = (card: typeof headerConfigs[number]) =>
        !card.headerConfig.moduleCard ? null : card.isAvailable ? 'available' : card.isOrderable ? 'orderable' : 'upgrade';

    // Split the orderable and upgrade module cards off for their own sections
    const regularConfigs = headerConfigs.filter(card => sectionOf(card) === null || sectionOf(card) === 'available');
    const orderableConfigs = headerConfigs.filter(card => sectionOf(card) === 'orderable');
    const upgradeConfigs = headerConfigs.filter(card => sectionOf(card) === 'upgrade');

    // Get the price information of an orderable module feature
    const orderableModuleOf = (feature?: string) =>
        moduleFeatures?.orderableModules.find(module => module.feature === feature);

    // Render all pageHeaders for the current page
    return (
        <>
            {regularConfigs.map((card, position) => {
                const { headerConfig, index, inPackage, isAvailable } = card;

                // Module cards without an available switch show an order link
                const isDisabled = headerConfig.moduleCard ? !isAvailable : !inPackage;

                // Show a section header above the first module card of each section
                const section = sectionOf(card);
                const previousSection = position > 0 ? sectionOf(regularConfigs[position - 1]) : null;
                const showSectionHeader = section !== null && section !== previousSection;

                // Configure the tabs of a pageheader
                const tabs = headerConfig.tabs?.map((tab, tabIndex) => {
                    if (typeof tab.componentName === 'string' && tab.componentName in componentMapping) {
                        const Component = componentMapping[tab.componentName as keyof typeof componentMapping];

                        const uniqueKey = `${tab.componentName}-${tab.label || tabIndex}`;

                        return {
                            ...tab,
                            component: Component ? (
                                <Component
                                    key={uniqueKey}
                                    {...(tab.componentProps || {})}
                                />
                            ) : null
                        };
                    }
                    return {
                        ...tab,
                        component: null
                    };
                }) || [];

                // Resolve the optional counter component shown next to the title
                const CounterComponent = (
                    typeof headerConfig.counterComponentName === 'string' &&
                    headerConfig.counterComponentName in componentMapping
                ) ? componentMapping[headerConfig.counterComponentName as keyof typeof componentMapping] : null;

                return (
                    <React.Fragment key={`${currentPageRoute}-${index}`}>
                        {showSectionHeader && (
                            <div className='container-filled'>
                                <div className={`module-section-header ${position === 0 ? 'first' : ''}`}>
                                    <h3>{t(`nav.module_section_${section}_header`)}</h3>
                                    <p className='explanation-text'>{t(`nav.module_section_${section}_description`)}</p>
                                </div>
                            </div>
                        )}
                        <PageHeader
                            tabs={tabs}
                            title={headerConfig.title}
                            explanationText={headerConfig.explanationText}
                            explanationImage={headerConfig.explanationImage}
                            explanationVideoUrl={headerConfig.explanationVideoUrl}
                            switchButton={headerConfig.switchButton}
                            switchButtonApiUrl='patch_environment_feature'
                            requiredFeature={headerConfig.requiredFeature}
                            counter={CounterComponent && !isDisabled ? <CounterComponent /> : undefined}
                            disabled={isDisabled}
                            smallTitle={headerConfig.moduleCard}
                            basePath={currentPageRoute}
                            syncTabsWithUrl={tabs.length > 1}
                        />
                    </React.Fragment>
                );
            })}

            {/* Render the orderable module cards with their price and order link */}
            {orderableConfigs.length > 0 && (
                <>
                    <div className='container-filled'>
                        <div className={`module-section-header ${regularConfigs.length === 0 ? 'first' : ''}`}>
                            <h3>{t('nav.module_section_orderable_header')}</h3>
                            <p className='explanation-text'>{t('nav.module_section_orderable_description')}</p>
                        </div>
                    </div>
                    {orderableConfigs.map(({ headerConfig, index }) => {
                        const orderableModule = orderableModuleOf(headerConfig.requiredFeature);
                        return (
                            <div className='container-filled' key={`${currentPageRoute}-${index}`}>
                                <ModuleOrderCard
                                    feature={headerConfig.requiredFeature!}
                                    title={t(headerConfig.title)}
                                    description={headerConfig.explanationText ? t(headerConfig.explanationText) : undefined}
                                    priceType={orderableModule?.price_type}
                                    flatPrice={orderableModule?.flat_price}
                                    pricePerSeat={orderableModule?.price_per_seat}
                                />
                            </div>
                        );
                    })}
                </>
            )}

            {/* Render the higher package module cards as a compact grid */}
            {upgradeConfigs.length > 0 && (
                <div className='container-filled'>
                    <div className={`module-section-header ${regularConfigs.length === 0 && orderableConfigs.length === 0 ? 'first' : ''}`}>
                        <h3>{t('nav.module_section_upgrade_header')}</h3>
                        <p className='explanation-text'>{t('nav.module_section_upgrade_description')}</p>
                    </div>
                    <div className='module-upgrade-cards'>
                        {upgradeConfigs.map(({ headerConfig, index }) => (
                            <div className='module-upgrade-card'
                                 key={`${currentPageRoute}-${index}`}
                                 title={headerConfig.explanationText ? t(headerConfig.explanationText) : undefined}>
                                <h5>{t(headerConfig.title)}</h5>
                                {headerConfig.explanationText && (
                                    <p>{t(headerConfig.explanationText)}</p>
                                )}
                                <ModuleOrderLink feature={headerConfig.requiredFeature} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default SettingsPage;